/** Angular-unit helpers for scene drawing (docs/SCENES.md). */

import { RENDER_H, RENDER_W } from './types';

export interface Geom {
  ppd: number;
  hfov: number;
  vfov: number;
  /** Canvas x for a horizontal angle (deg, +right). */
  X(deg: number): number;
  /** Canvas y for a vertical angle (deg, +up). Canvas y grows downwards. */
  Y(deg: number): number;
  /** Pixels for an angular size. */
  P(deg: number): number;
}

export function geom(hfov: number): Geom {
  const ppd = RENDER_W / hfov;
  return {
    ppd,
    hfov,
    vfov: (hfov * RENDER_H) / RENDER_W,
    X: (deg) => RENDER_W / 2 + deg * ppd,
    Y: (deg) => RENDER_H / 2 - deg * ppd,
    P: (deg) => deg * ppd,
  };
}

/** Visual angle (deg) of a physical size s at distance d (same units). */
export function angularSize(s: number, d: number): number {
  return (2 * Math.atan(s / (2 * d)) * 180) / Math.PI;
}

/** System sans stack; cap height is taken as 0.72 em. */
export const SCENE_FONT = 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
const CAP_RATIO = 0.72;

export function fontPxForCap(capPx: number): number {
  return capPx / CAP_RATIO;
}

export function setFont(ctx: CanvasRenderingContext2D, capPx: number, weight: string | number = 400): void {
  ctx.font = `${weight} ${fontPxForCap(capPx).toFixed(2)}px ${SCENE_FONT}`;
}

/**
 * Set a font by cap height, shrinking it if the text would exceed maxWidth.
 * Returns the cap height actually used.
 */
export function fitFont(
  ctx: CanvasRenderingContext2D,
  text: string,
  capPx: number,
  maxWidth: number,
  weight: string | number = 400,
): number {
  setFont(ctx, capPx, weight);
  const w = ctx.measureText(text).width;
  if (w <= maxWidth) return capPx;
  const cap = (capPx * maxWidth) / w;
  setFont(ctx, cap, weight);
  return cap;
}

export function roundRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.arcTo(x + w, y, x + w, y + rr, rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.arcTo(x + w, y + h, x + w - rr, y + h, rr);
  ctx.lineTo(x + rr, y + h);
  ctx.arcTo(x, y + h, x, y + h - rr, rr);
  ctx.lineTo(x, y + rr);
  ctx.arcTo(x, y, x + rr, y, rr);
  ctx.closePath();
}

export interface PxRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Canvas rectangle for an angular box x0..x1 (deg, +right) and y0..y1 (deg, +up). */
export function rectDeg(g: Geom, x0: number, x1: number, y0: number, y1: number): PxRect {
  return { x: g.X(x0), y: g.Y(y1), w: (x1 - x0) * g.ppd, h: (y1 - y0) * g.ppd };
}

export function fillRectDeg(ctx: CanvasRenderingContext2D, g: Geom, x0: number, x1: number, y0: number, y1: number, style: string | CanvasGradient): void {
  const r = rectDeg(g, x0, x1, y0, y1);
  ctx.fillStyle = style;
  ctx.fillRect(r.x, r.y, r.w, r.h);
}

export function ellipseDeg(ctx: CanvasRenderingContext2D, g: Geom, cx: number, cy: number, rx: number, ry: number): void {
  ctx.beginPath();
  ctx.ellipse(g.X(cx), g.Y(cy), rx * g.ppd, ry * g.ppd, 0, 0, Math.PI * 2);
}

export function polygonDeg(ctx: CanvasRenderingContext2D, g: Geom, points: ReadonlyArray<readonly [number, number]>): void {
  ctx.beginPath();
  points.forEach(([x, y], i) => {
    if (i === 0) ctx.moveTo(g.X(x), g.Y(y));
    else ctx.lineTo(g.X(x), g.Y(y));
  });
  ctx.closePath();
}

/** Draw text with its cap height in degrees; (x, y) is the baseline start in degrees. */
export function textDeg(
  ctx: CanvasRenderingContext2D,
  g: Geom,
  text: string,
  x: number,
  y: number,
  capDeg: number,
  style: string,
  weight: string | number = 400,
  align: CanvasTextAlign = 'left',
): void {
  setFont(ctx, capDeg * g.ppd, weight);
  ctx.fillStyle = style;
  ctx.textAlign = align;
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(text, g.X(x), g.Y(y));
}

/** Greedy word wrap for the current ctx.font. */
export function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let line = '';
  for (const w of words) {
    const candidate = line ? `${line} ${w}` : w;
    if (ctx.measureText(candidate).width <= maxWidth || !line) line = candidate;
    else {
      lines.push(line);
      line = w;
    }
  }
  if (line) lines.push(line);
  return lines;
}

/** Deterministic pseudo-random in [0, 1) for scene details. */
export function hash01(n: number): number {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
}

export function withShadow(
  ctx: CanvasRenderingContext2D,
  color: string,
  blur: number,
  offsetY: number,
  draw: () => void,
): void {
  ctx.save();
  ctx.shadowColor = color;
  ctx.shadowBlur = blur;
  ctx.shadowOffsetY = offsetY;
  draw();
  ctx.restore();
}
